import { Box, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import type { ReactElement } from "react";
import type { PublicDestinationReportSections } from "@otalytics/shared";

import { DestinationRevenue } from "./destination-revenue";
import { LeaderboardTable } from "./leaderboard-table";
import { TopOperators } from "./top-operators";

/**
 * The unlocked report document: the full leaderboard, revenue, every operator,
 * this month's movers, and the insights.
 *
 * Server-safe and shared by both surfaces that may render it — the paid
 * `/reports/[slug]` issue, and the public destination page in the one case
 * where nothing was worth gating (a page too thin to withhold half of). One
 * implementation, so a subscriber and a thin-page visitor read the same
 * artifact rather than two documents that drift apart.
 */
export function ReportSections({
  full,
  destinationName,
  leaderboardNote,
  operatorBlurb,
  operatorLink,
}: {
  full: PublicDestinationReportSections;
  destinationName: string;
  /** City-page explanation under the leaderboard heading. */
  leaderboardNote?: string;
  /** Replaces the default operator blurb on the city page. */
  operatorBlurb?: string;
  operatorLink?: { href: string; label: string } | null;
}): ReactElement {
  /* Only a pair earns a divider — a lone column must not carry a stray edge. */
  const hasBothMoverColumns =
    full.movers.risers.length > 0 && full.movers.fallers.length > 0;

  /* The churn insight carries the same entered/dropped pair as the movers
     footnote. It only fires above a threshold on the API side, so this asks
     whether it actually did rather than assuming it. */
  const statesChurnInInsights = full.insights.some(
    (insight) => insight.id === "churn"
  );

  /* The rank column skips numbers wherever the first page held something that
     was not a tour. Stated up front, beside the heading, because the first gap
     arrives within a screen of it — and left unstated it reads as a bug in a
     section called "Every tour on the first page". */
  const lastRankedPosition =
    full.leaderboard.length > 0
      ? full.leaderboard[full.leaderboard.length - 1]!.position
      : 0;
  const leaderboardSkipsPositions =
    full.leaderboard.length > 0 && full.leaderboard.length !== lastRankedPosition;

  return (
    <Stack gap={{ base: 10, md: 12 }}>
      {/* Spine first — same heading, same position, as the gated branch. The
          unlock fills this table from 3 rows to all of them; it does not
          reshuffle the page under the reader.

          The extra bottom margin is the document's rhythm: ~80px marks the
          end of the artifact, the 48px Stack gap separates the readings of
          it. A uniform gap between five unequal blocks is not rhythm. */}
      <Box mb={{ base: 4, md: 8 }}>
        <Heading
          as="h2"
          fontFamily="var(--font-display)"
          fontSize={{ base: "26px", md: "34px" }}
          fontWeight="600"
          letterSpacing="-0.02em"
          color="var(--color-text-primary)"
          mb={leaderboardSkipsPositions ? 1 : 2}
        >
          Every tour on the first page
        </Heading>
        {leaderboardNote && (
          <Text
            fontSize={{ base: "15px", md: "16px" }}
            lineHeight="1.6"
            color="var(--color-text-secondary)"
            maxW="68ch"
            mb={3}
          >
            {leaderboardNote}
          </Text>
        )}
        {leaderboardSkipsPositions && (
          <Text
            fontFamily="var(--font-geist-mono)"
            fontSize="11px"
            color="var(--color-text-tertiary)"
            mb={3}
          >
            {full.leaderboard.length} tours across the first{" "}
            {lastRankedPosition} places
          </Text>
        )}
        <LeaderboardTable rows={full.leaderboard} />
      </Box>

      {full.revenue && (
        <DestinationRevenue
          revenue={full.revenue}
          destinationName={destinationName}
        />
      )}

      {full.operators && (
        <TopOperators
          operators={full.operators}
          title="Who owns the first page"
          blurb={
            operatorBlurb ??
            "Every operator on this page, with its share of the page's recent review activity and of its estimated turnover."
          }
          link={operatorLink}
        />
      )}

      {(full.movers.risers.length > 0 || full.movers.fallers.length > 0) && (
        <Box>
          <Heading
            as="h2"
            fontFamily="var(--font-display)"
            fontSize={{ base: "20px", md: "22px" }}
            fontWeight="600"
            letterSpacing="-0.02em"
            color="var(--color-text-primary)"
            mb={2}
          >
            Who moved this month
          </Heading>
          <Flex
            gap={{ base: 6, md: 10 }}
            direction={{ base: "column", md: "row" }}
          >
            {full.movers.risers.length > 0 && (
              <Box flex="1 1 0" minW={0}>
                <Text as="h3" fontSize="14px" fontWeight="600" mb={2} color="var(--color-success-strong)">
                  Climbing
                </Text>
                <LeaderboardTable rows={full.movers.risers} variant="compact" />
              </Box>
            )}
            {full.movers.fallers.length > 0 && (
              /* The two tables shared row baselines with nothing between them,
                 so rank 2 on the left read as paired with rank 20 on the right.
                 A hairline makes them two panels of one thing. */
              <Box
                flex="1 1 0"
                minW={0}
                borderLeft={
                  hasBothMoverColumns
                    ? { base: "none", md: "1px solid var(--color-border)" }
                    : undefined
                }
                pl={hasBothMoverColumns ? { base: 0, md: 10 } : undefined}
              >
                <Text as="h3" fontSize="14px" fontWeight="600" mb={2} color="var(--color-error-strong)">
                  Slipping
                </Text>
                <LeaderboardTable rows={full.movers.fallers} variant="compact" />
              </Box>
            )}
          </Flex>
          {/* The insight panel below already says "N tours broke onto the first
              page this month and N dropped off" whenever churn clears its
              threshold, so printing the same two numbers here in mono is the
              same fact twice within a screen. Shown only when that insight did
              not fire — then this is the only place the counts appear. */}
          {!statesChurnInInsights && (
            <Text
              fontFamily="var(--font-geist-mono)"
              fontSize="11px"
              color="var(--color-text-tertiary)"
              mt={4}
              pt={3}
              borderTop="1px solid var(--color-border-subtle)"
            >
              {full.movers.enteredCount} entered · {full.movers.droppedCount}{" "}
              dropped out
            </Text>
          )}
        </Box>
      )}

      {full.insights.length > 0 && (
        <Box>
          <Heading
            as="h2"
            fontFamily="var(--font-display)"
            fontSize={{ base: "20px", md: "22px" }}
            fontWeight="600"
            letterSpacing="-0.02em"
            color="var(--color-text-primary)"
            mb={2}
          >
            What the page tells you
          </Heading>
          <Stack as="ul" gap={0}>
            {full.insights.map((insight) => (
              <Text
                as="li"
                listStyleType="none"
                key={insight.id}
                fontSize={{ base: "15px", md: "16px" }}
                color="var(--color-text-secondary)"
                lineHeight="1.6"
                py={3}
                borderBottom="1px solid var(--color-border-subtle)"
              >
                <InsightLine text={insight.title} />
              </Text>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

/**
 * Numeric tokens inside an insight, set in mono at full ink.
 *
 * Every insight string is a sentence built around a figure the scrape actually
 * measured. Rendering it as flat secondary prose buries the one part a reader
 * scans for, so the figures are lifted to primary ink in the mono face while
 * the sentence around them stays secondary.
 */
const INSIGHT_FIGURE = /(€?\d+(?:[.,]\d+)*(?:%|×)?)/g;

export function InsightLine({ text }: { text: string }): ReactElement {
  return (
    <>
      {text.split(INSIGHT_FIGURE).map((part, index) =>
        // Odd indices are the captured figures; even indices are the prose
        // between them. Split with a capturing group guarantees that shape.
        index % 2 === 1 ? (
          <Text
            as="span"
            key={index}
            fontFamily="var(--font-geist-mono)"
            color="var(--color-text-primary)"
            fontWeight="500"
          >
            {part}
          </Text>
        ) : (
          part
        )
      )}
    </>
  );
}
